import PropTypes from "prop-types";

// @mui material components
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import Icon from "@mui/material/Icon";

// Material Kit 2 React components
import MKBox from "components/MKBox";
import MKTypography from "components/MKTypography";

// Airbnb charges ~15% more (guest service fee in split-fee model)
const AIRBNB_MARKUP = 0.15;

function PriceRow({ label, value, bold, color, strikethrough }) {
  return (
    <MKBox display="flex" justifyContent="space-between" mb={1}>
      <MKTypography
        variant="body2"
        fontWeight={bold ? "bold" : "regular"}
        color={color || (bold ? "dark" : "text")}
      >
        {label}
      </MKTypography>
      <MKTypography
        variant="body2"
        fontWeight={bold ? "bold" : "regular"}
        color={color || (bold ? "dark" : "text")}
        sx={strikethrough ? { textDecoration: "line-through", opacity: 0.6 } : {}}
      >
        ${value.toLocaleString()}
      </MKTypography>
    </MKBox>
  );
}

function PricingSummary({ pricing }) {
  if (!pricing) return null;

  const {
    nights,
    accommodationTotal,
    cleaningFee,
    vat,
    vatPercent,
    total,
    minimumDeposit,
    depositPercent,
    balanceDue,
    balanceCash,
    cashDiscountPercent,
  } = pricing;
  const depPct = Math.round((depositPercent || 0.5) * 100);
  const cashPct = Math.round((cashDiscountPercent || 0.06) * 100);
  const airbnbEstimate = Math.round(total * (1 + AIRBNB_MARKUP));

  return (
    <MKBox component="section" py={4}>
      <Container maxWidth="sm">
        {/* Price breakdown */}
        <MKBox bgColor="grey-100" borderRadius="xl" p={3}>
          <MKTypography variant="h5" mb={2}>
            Price Breakdown
          </MKTypography>
          <PriceRow
            label={`$${Math.round(accommodationTotal / nights)} × ${nights} night${
              nights > 1 ? "s" : ""
            }`}
            value={accommodationTotal}
          />
          <PriceRow label="Cleaning fee" value={cleaningFee} />
          <PriceRow label={`VAT (${Math.round((vatPercent || 0.13) * 100)}%)`} value={vat} />
          <Divider sx={{ my: 2 }} />
          <PriceRow label="Your total (save 15%)" value={total} bold color="success" />
          <PriceRow label="Estimated Airbnb price" value={airbnbEstimate} strikethrough />

          <Divider sx={{ my: 2 }} />

          {/* 1. Deposit */}
          <MKBox display="flex" alignItems="center" gap={0.5} mb={0.5}>
            <MKTypography variant="body2" fontWeight="bold" color="dark">
              1.
            </MKTypography>
            <MKTypography variant="body2" fontWeight="bold" color="dark">
              Deposit due after approval ({depPct}%):
            </MKTypography>
            <MKTypography variant="body2" fontWeight="bold" color="dark" ml="auto">
              ${minimumDeposit.toLocaleString()}
            </MKTypography>
          </MKBox>

          {/* 2. Balance */}
          <MKBox display="flex" alignItems="center" gap={0.5} mt={2} mb={1}>
            <MKTypography variant="body2" fontWeight="bold" color="dark">
              2.
            </MKTypography>
            <MKTypography variant="body2" fontWeight="bold" color="dark">
              Balance on arrival:
            </MKTypography>
          </MKBox>
          <MKBox pl={3}>
            <MKBox display="flex" alignItems="center" gap={0.5} mb={0.5}>
              <Icon sx={{ fontSize: "1rem", color: "success.main" }}>payments</Icon>
              <MKTypography variant="body2" fontWeight="medium" color="success">
                Pay cash: ${balanceCash.toLocaleString()} (save {cashPct}%)
              </MKTypography>
            </MKBox>
            <MKBox display="flex" alignItems="center" gap={0.5}>
              <Icon sx={{ fontSize: "1rem", color: "dark" }}>credit_card</Icon>
              <MKTypography variant="body2" fontWeight="medium" color="dark">
                Pay card: ${balanceDue.toLocaleString()}
              </MKTypography>
            </MKBox>
          </MKBox>
        </MKBox>
      </Container>
    </MKBox>
  );
}

PriceRow.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  bold: PropTypes.bool,
  color: PropTypes.string,
  strikethrough: PropTypes.bool,
};

PricingSummary.propTypes = {
  pricing: PropTypes.shape({
    nights: PropTypes.number,
    accommodationTotal: PropTypes.number,
    cleaningFee: PropTypes.number,
    vat: PropTypes.number,
    vatPercent: PropTypes.number,
    depositPercent: PropTypes.number,
    total: PropTypes.number,
    minimumDeposit: PropTypes.number,
    balanceDue: PropTypes.number,
    balanceCash: PropTypes.number,
    cashDiscountPercent: PropTypes.number,
  }),
};

export default PricingSummary;
